// plan-05 S01: hex grid geometry — verbatim port from v1

export interface Hex {
  col: number
  row: number
  cx: number
  cy: number
  seqNum: number
  poly?: SVGPolygonElement
}

export interface HexGrid {
  hexes: Hex[]
  ordered: Hex[]
  centerHex: Hex
  total: number
  width: number
  height: number
  hexByPos: Map<string, Hex>
  hexSize: number
}

const HEX_SIZE = 11
const GAP = 1.5
const R = HEX_SIZE + GAP / 2
const HW = R
const HH = R * Math.sqrt(3) / 2
const COL_STEP = HW * 1.5 + GAP * 0.866
const ROW_STEP = HH * 2 + GAP
const GRID_COLS = 8

const FLAT_TOP_NEIGHBORS: [number, number][] = [
  [1, 0], [1, -1], [0, -1],
  [-1, 0], [-1, 1], [0, 1],
]

export function build(): HexGrid {
  const hexes: Hex[] = []
  const CIRCLE_R_PX = GRID_COLS * COL_STEP

  for (let col = -GRID_COLS; col <= GRID_COLS; col++) {
    const cx = col * COL_STEP
    const rowOffset = (Math.abs(col) % 2 === 1) ? HH : 0
    const halfRows = Math.floor(
      (Math.sqrt(Math.max(0, CIRCLE_R_PX * CIRCLE_R_PX - cx * cx)) + HH) / ROW_STEP,
    )
    for (let row = -halfRows; row <= halfRows; row++) {
      const cy = row * ROW_STEP + rowOffset
      if (Math.sqrt(cx * cx + cy * cy) <= CIRCLE_R_PX * 0.97) {
        hexes.push({ col, row, cx, cy, seqNum: -1 })
      }
    }
  }

  // normalize to positive coordinates
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const h of hexes) {
    if (h.cx < minX) minX = h.cx
    if (h.cy < minY) minY = h.cy
    if (h.cx > maxX) maxX = h.cx
    if (h.cy > maxY) maxY = h.cy
  }
  const padX = HEX_SIZE + 4
  const padY = HEX_SIZE + 4
  const W = maxX - minX + padX * 2
  const H = maxY - minY + padY * 2

  for (const h of hexes) {
    h.cx -= minX - padX
    h.cy -= minY - padY
  }

  // canonical numbering: column buckets sorted left-to-right, then top-to-bottom
  const COL_TOL = COL_STEP * 0.5
  const colXs: number[] = []
  for (const h of hexes) {
    if (!colXs.some(x => Math.abs(x - h.cx) < COL_TOL)) colXs.push(h.cx)
  }
  colXs.sort((a, b) => a - b)

  const buckets: Hex[][] = colXs.map(() => [])
  for (const h of hexes) {
    const bi = colXs.findIndex(x => Math.abs(x - h.cx) < COL_TOL)
    buckets[bi].push(h)
  }
  for (const b of buckets) b.sort((a, c) => a.cy - c.cy)

  const ordered = buckets.flat()
  ordered.forEach((h, i) => { h.seqNum = i })
  const TOTAL = ordered.length

  // center hex
  const centerHex = hexes.find(h => h.col === 0 && h.row === 0)
    ?? hexes.reduce((best, h) => {
      const bd = best.cx * best.cx + best.cy * best.cy
      const hd = h.cx * h.cx + h.cy * h.cy
      return hd < bd ? h : best
    }, hexes[0])

  // col+row lookup for neighbor access
  const hexByPos = new Map<string, Hex>()
  for (const h of hexes) hexByPos.set(`${h.col},${h.row}`, h)

  return { hexes, ordered, centerHex, total: TOTAL, width: W, height: H, hexByPos, hexSize: HEX_SIZE }
}

export function getNeighbors(grid: HexGrid, h: Hex): Hex[] {
  return FLAT_TOP_NEIGHBORS
    .map(([dc, dr]) => grid.hexByPos.get(`${h.col + dc},${h.row + dr}`))
    .filter((n): n is Hex => n !== undefined)
}

export function getRing1(grid: HexGrid): Hex[] {
  return getNeighbors(grid, grid.centerHex)
}

export function getRing2(grid: HexGrid): Hex[] {
  const ring1Set = new Set(getRing1(grid).map(h => h.seqNum))
  const ring2: Hex[] = []
  for (const h of getRing1(grid)) {
    for (const nb of getNeighbors(grid, h)) {
      if (nb.seqNum !== grid.centerHex.seqNum && !ring1Set.has(nb.seqNum)) {
        if (!ring2.some(r => r.seqNum === nb.seqNum)) ring2.push(nb)
      }
    }
  }
  return ring2
}

export function getOutermost(grid: HexGrid): Hex[] {
  return grid.hexes.filter(h => getNeighbors(grid, h).length < 6)
}

export function hexPoints(cx: number, cy: number, size: number = HEX_SIZE): string {
  const pts: string[] = []
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i
    pts.push(`${cx + size * Math.cos(a)},${cy + size * Math.sin(a)}`)
  }
  return pts.join(' ')
}

export const HEX_SIZE_CONSTANT = HEX_SIZE
